import { useEffect, useState } from "react";
import Loader from "../../../components/theming/loader/Loader";
import { useParams } from "react-router-dom";
import { certificateQRCodeVerificationService } from "../../../api";
import { CertificateVerificationFail } from "../../../components/core/certificate-verification/CertificateVerificationFail";
import CertificateVerificationForm from "../../../components/core/certificate-verification/CertificateVerificationInputData";
import {
  CertificateVerificationInputData,
  CertificateVerificationResponseData,
} from "../../../models/CertificateQRCodeVerificationData";
import { CertificateVerificationData } from "../../../components/core/certificate-verification/CertificateVerificationData";

/**
 * Certificate QRCode Verification Component
 *
 * This component handles the certificate qr code verification.
 *
 * @returns JSX element representing the CertificateQRCodeVerification.
 */
export const CertificateQRCodeVerificationPage = () => {
  const { token } = useParams<{ token: string }>();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [tokenValidationIsLoading, setTokenValidationIsLoading] =
    useState<boolean>(true);
  const [certificateData, setCertificateData] =
    useState<CertificateVerificationResponseData | null>(null);
  const [certificateValidationIsLoading, setCertificateValidationIsLoading] =
    useState<boolean>(false);
  const [dataValid, setDataValid] = useState<boolean | null>(null);

  useEffect(() => {
    async function verifyToken() {
      try {
        await certificateQRCodeVerificationService.verifyCertificateQRCodeToken(token);
        setIsValidToken(true);
        setCertificateValidationIsLoading(true);
      } catch (error) {
        setIsValidToken(false);
      } finally {
        setTokenValidationIsLoading(false);
      }
    }
    verifyToken();
  }, [token]);

  const handleSubmit = async (values: CertificateVerificationInputData) => {
    const response_data =
      await certificateQRCodeVerificationService.retrieveVerifiedCertificateData(
        token,
        values
      );
    if (response_data === null) {
      setDataValid(false);
      // get back to page where data can be entered
    }
    setCertificateData(response_data);
    setCertificateValidationIsLoading(false);
  };

  const handleRetry = () => {
    setDataValid(null);
    setCertificateValidationIsLoading(true);
  };

  return tokenValidationIsLoading ? (
    <Loader />
  ) : isValidToken === false || dataValid === false ? (
    <CertificateVerificationFail onRetry={handleRetry} isValidToken={isValidToken} />
  ) : certificateValidationIsLoading && !certificateData ? (
    <CertificateVerificationForm onSubmit={handleSubmit} />
  ) : isValidToken && certificateData ? (
    <CertificateVerificationData certificateData={certificateData} />
  ) : null;
};
